'use client'

import { CarouselContextProvider, useButtonsProps } from '@web/components/cf-carousel/context'
import { Container } from '@web/components/cf-carousel/container'
import { ChevronLeftIcon, ChevronRightIcon } from 'primitives/icons'
import { Link } from '@repo/web-ui/components/link'
import type { AlgoliaTemplateHit } from '@repo/studio-components-templates/context/templates-page'
import Image from 'next/image'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { tw } from 'utils/classnames'

interface SectionContentProps {
  hits: AlgoliaTemplateHit[]
}

export function SectionContent({ hits }: SectionContentProps) {
  return (
    <CarouselContextProvider>
      <div className="relative">
        <NavigationButton direction="left">
          <ChevronLeftIcon className="text-cf-grey-600 size-8" />
        </NavigationButton>
        <NavigationButton direction="right">
          <ChevronRightIcon className="text-cf-grey-600 size-8" />
        </NavigationButton>
        <Container>
          {hits.map(hit => (
            <TemplateCard key={hit.objectID} hit={hit} />
          ))}
        </Container>
      </div>
    </CarouselContextProvider>
  )
}

interface TemplateCardProps {
  hit: AlgoliaTemplateHit
}

export function TemplateCard({ hit }: TemplateCardProps) {
  const aspectRatio = hit.width && hit.height ? hit.width / hit.height : 1
  const aspectRatioWidth = 190 * aspectRatio
  return (
    <Link
      href={`${AppRoutes.STUDIO_TOOL_CREATE}?template_category=template&content_template_id=${hit.objectID}`}
      className="flex-shrink-0 snap-start"
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className="rounded-2 overflow-hidden">
        <Image
          src={hit.imageUrl}
          alt={`${hit.title} template`}
          className="bg-cf-dreamy-cloud-500 shrink-0 object-cover transition-all duration-150 ease-linear hover:scale-110"
          width={aspectRatioWidth}
          height={190}
          style={{
            height: 190,
            width: aspectRatioWidth,
          }}
        />
      </div>
      <p
        className="text-12 text-gray-300-600 overflow-hidden truncate whitespace-nowrap p-1 pt-2"
        style={{
          width: aspectRatioWidth,
        }}
      >
        {hit.title}
      </p>
    </Link>
  )
}

export function NavigationButton({
  children,
  className,
  direction,
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  direction: 'left' | 'right'
}) {
  const { nextButtonProps, prevButtonProps } = useButtonsProps()

  return (
    // oxlint-disable-next-line react/button-has-type
    <button
      disabled={direction === 'left' ? prevButtonProps.disabled : nextButtonProps.disabled}
      onClick={direction === 'left' ? prevButtonProps.onClick : nextButtonProps.onClick}
      aria-label={direction === 'left' ? 'Previous templates' : 'Next templates'}
      key={direction === 'left' ? 'previous-button' : 'next-button'}
      name={direction === 'left' ? 'prev' : 'next'}
      className={tw(
        'absolute z-10 flex h-[calc(100%-6px)] w-14 items-center from-transparent to-[#f6f7fb] to-80% transition-all duration-150 disabled:hidden [&_*]:opacity-90 hover:[&_*]:opacity-100',
        direction === 'left' ? 'inset-0 justify-start' : 'right-0 top-0 justify-end',
        direction === 'left' ? 'bg-gradient-to-l' : 'bg-gradient-to-r',
        className,
      )}
    >
      {children}
    </button>
  )
}
